import { Injectable, inject } from "@angular/core";
import { Utilities } from "@tylertech/shared-module";
import
{
    GridMetadata,
    IGridViewMetadata,
    IMetadataAction,
    KeyValuePair,
    MetadataSummary,
    PropertyMetadata,
    SelectionMode,
} from "@tylertech/tyler-api-types";
import { MetadataIntermediary, MetadataKey } from "../../interfaces";
import
{
    GridFetchType,
    GridLookupService,
    GridMetadataService,
    GridSelectionService,
    MetadataPropertyService,
    MetadataService,
    MetadataViewService,
    ProviderStorageService,
} from "..";
import { ObjectHelpers } from "../../utilities";

@Injectable({ providedIn: "root" })
export class GridProcessingService
{
    private $providerStorageService = inject(ProviderStorageService);
    private $metadataService = inject(MetadataService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataViewService = inject(MetadataViewService);
    private $gridLookupService = inject(GridLookupService);
    private $gridMetadataService = inject(GridMetadataService);
    private $gridSelectionService = inject(GridSelectionService);


    buildGridRows(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        multiselectActions?: IMetadataAction[],
        filteredColumns?: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        if (!viewMetadata.SelectionMode)
            viewMetadata.SelectionMode = SelectionMode.None;
        if (entities.length === 0 || (filteredColumns && filteredColumns.every(x => x.indexOf(".") < 0)))
        {
            return this.loadRows(
                entities,
                viewMetadata,
                multiselectActions,
                filteredColumns,
                includeLookups,
                forFilter
            );
        }
        return this.$metadataService
            .AugmentModels(
                viewMetadata.GridProperties.ClassMetadata,
                entities,
                viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected"
            )
            .then(() =>
            {
                return this.loadChildren(
                    entities,
                    viewMetadata,
                    multiselectActions,
                    filteredColumns,
                    includeLookups,
                    forFilter
                );
            });
    }

    loadRows(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        multiselectActions: IMetadataAction[],
        filteredColumns: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        const providerId = viewMetadata.GridProperties["$providerId"];
        const filteredColumnsMap = new Map<number, Set<string>>();
        if (filteredColumns)
        {
            filteredColumns.forEach(propPath =>
            {
                const parts = propPath.split('.');
                parts.forEach((segment, idx) =>
                {
                    if (!filteredColumnsMap.has(idx))
                        filteredColumnsMap.set(idx, new Set<string>());
                    filteredColumnsMap.get(idx).add(segment);
                });
            });
        }

        const rows = entities
            .filter(x => (forFilter || !this.$providerStorageService.Scrolling[providerId] || !x["$processed"]) && (Utilities.IsNullOrUndefined(x.SerializedRowState) || x.SerializedRowState !== "Deleted"))
            .map((r, i) => this.createDataRow(r, i, viewMetadata, viewMetadata.SelectProperty, filteredColumnsMap, includeLookups));

        if (Utilities.IsNullOrUndefined(filteredColumns))
        {
            const filteredList = entities.filter(
                x => Utilities.IsNullOrUndefined(x.SerializedRowState) || x.SerializedRowState !== "Deleted"
            );
            if (filteredList.length > 0 && (!forFilter || !viewMetadata["$columns"]))
                viewMetadata["$columns"] = this.defineColumns(filteredList[0], viewMetadata);
            const actions = viewMetadata.Actions;
            if (!filteredColumns)
                viewMetadata["$selectOnly"] =
                    ((!actions || actions.length === 0) && viewMetadata.SelectionMode !== SelectionMode.None) ||
                    (actions?.length === 1 && multiselectActions && multiselectActions[0]?.ReturnPath === "Multiselect");
        }

        if (includeLookups?.length === 0)
            return Promise.resolve(rows);

        const lookupPromise = this.$gridLookupService.loadLookups(viewMetadata.GridProperties.ClassMetadata, rows, filteredColumns, includeLookups, entities);

        if (!filteredColumns)
            return Promise.resolve(rows);
        else
            return lookupPromise;
    }

    defineColumns(entity: any, viewMetadata: IGridViewMetadata): any[]
    {
        const self = this;
        const metadata = viewMetadata.GridProperties.ClassMetadata;
        const intermediaries = this.$metadataService.FlattenProperties(metadata, entity.$$Parent, entity);
        return intermediaries
            .sort((a, b) => a.property.RenderPosition - b.property.RenderPosition)
            .map(x => createDataColumn(x));
        function createDataColumn(property: MetadataIntermediary): any
        {
            const propertyName = property.property.Name || property.property.Label.replace(/ /g, "");
            const column = {};
            column["Name"] = propertyName;
            if (property.property["$parentPropertyName"])
                column["ParentPropertyName"] = property.property["$parentPropertyName"];
            column["Label"] = self.$metadataService.EvaluateString(
                property.property.Label,
                property.model.$$Parent,
                property.model
            ).trim();
            column["DataType"] = self.$gridMetadataService.getDataType(property.property);
            column["IsCurrency"] = self.$gridMetadataService.isCurrency(property.property);
            column["IsIcon"] = !Utilities.IsNullOrUndefined(property.property.ActionMetadata) && !Utilities.IsNullOrUndefined(property.property.ActionMetadata.Icon);
            column["IsCellAction"] = !Utilities.IsNullOrUndefined(property.property.ActionMetadata) || property.property.LinkAction;
            column["LookupType"] = Utilities.IsNullOrUndefined(property.property.Lookup) || !Utilities.IsNullOrUndefined(property.property.LookupProperty) ? null : property.property.Lookup;
            column["IsFilterable"] = self.isFilterable(property.property, self.$providerStorageService.GridFetchType[viewMetadata.GridProperties["$providerId"]] === GridFetchType.ServerStore);
            column["AlwaysHidden"] = self.alwaysHidden(property);
            if (!column["AlwaysHidden"] && property.property.DefaultPinned)
                column["DefaultPinned"] = property.property.DefaultPinned;
            column["WidthPercentage"] = property.property.DrawWidth === 100 || property.property.DrawWidth === -1 ? 0 : property.property.DrawWidth;
            column["HasTotal"] = property.property.SummaryType === MetadataSummary.Sum;
            column["Icon"] = Utilities.IsNullOrUndefined(property.property.ActionMetadata)
                ? ""
                : property.property.ActionMetadata.Icon;
            column["Visible"] = self.columnVisible(property) && property.property.DrawWidth !== -2;
            column["MetadataAction"] = Utilities.IsNullOrUndefined(property.property.ActionMetadata)
                ? undefined
                : property.property.ActionMetadata;
            column["LinkAction"] = property.property.LinkAction;
            if (property.property.Scale || property.property.Scale === 0)
                column["DigitsInfo"] = `1.${property.property.Scale}-${property.property.Scale}`;
            if (property.property.ScaleProperty)
            {
                const scale = self.$metadataService.DependentModuleValue(property.property.ScaleProperty, entity.$$Parent, entity);
                column["DigitsInfo"] = `1.${scale}-${scale}`;
            }
            return column;
        }
    }

    createDataRow(entity: any, index: number, viewMetadata: IGridViewMetadata, selectProperty: string, filteredColumnsMap?: Map<number, Set<string>>, includeLookups?: string[]): any
    {
        const self = this;
        const intermediaries = self.$metadataService.FlattenProperties(viewMetadata.GridProperties.ClassMetadata, entity.$$Parent, entity, filteredColumnsMap);
        if (Utilities.IsNullOrUndefined(includeLookups) || includeLookups.length > 0)
            self.$gridLookupService.setPropertyIds(entity, intermediaries, viewMetadata.GridProperties.ClassMetadata, includeLookups);
        const item = {};
        intermediaries.forEach((x) => createDataColumn(x));
        if (!item["Id"])
        {
            item["Id"] = ObjectHelpers.GetId(viewMetadata.GridProperties.IdProperty, entity, index);
            if (!Utilities.IsNullOrUndefinedOrZero(entity["$$AddedRowId"]) && this.$metadataViewService.IsProjectionGrid(viewMetadata))
            {
                entity[viewMetadata.GridProperties.IdProperty] = item["Id"];
                if (entity["$$editEntity"])
                    entity["$$editEntity"]["$$AddedRowId"] = item["Id"];
            }
        }

        item["Selected"] = setSelected(item["Id"]);
        if (!filteredColumnsMap || filteredColumnsMap.size === 0)
            entity["$processed"] = true;
        item["$$model"] = entity;
        if (entity["$addedCellEdit"])
            item["$cellEditAdded"] = true;
        item["Commands"] = rowCommands(viewMetadata.GridProperties, entity);
        if (viewMetadata.GridProperties.FeatureAction)
        {
            const action: IMetadataAction = Utilities.deepCopy(viewMetadata.GridProperties.FeatureAction);
            action.Caption = self.$metadataService.EvaluateString(action.Label, entity.$$Parent, entity);
            action.Hidden = self.$metadataPropertyService.isHidden(
                <PropertyMetadata>{
                    HiddenProperty: action.HiddenProperty,
                    Hidden: action.Hidden
                },
                entity,
                entity.$$Parent
            );
            action.Disabled = self.$metadataPropertyService.isDisabled(
                <PropertyMetadata>{
                    DisabledProperty: action.DisabledProperty,
                    Disabled: action.Disabled
                },
                entity,
                entity.$$Parent
            );
            item["FeatureAction"] = action;
        }

        function createDataColumn(intermediary: MetadataIntermediary): void
        {
            const propertyName = intermediary.property.Name || intermediary.property.Label.replace(/ /g, "");
            if (Utilities.IsNullOrUndefined(intermediary.property.ActionMetadata))
                item[propertyName] = setElementValue(intermediary);
            else
                item[propertyName] = Utilities.IsNullOrUndefined(intermediary.property.ActionMetadata.Label)
                    ? setElementValue(intermediary)
                    : intermediary.property.ActionMetadata.Label;
            if (Utilities.IsNullOrUndefined(item[propertyName]))
                item[propertyName] = intermediary.property.InputType === "Date" ? null : "";
        }
        function setElementValue(intermediary: MetadataIntermediary): string
        {
            if (intermediary.property.LookupProperty)
            {
                const list: KeyValuePair<MetadataKey, string>[] = self.$metadataService.DependentModuleValue(
                    intermediary.property.LookupProperty,
                    intermediary.parent,
                    intermediary.model
                );
                if (Utilities.IsNullOrUndefinedOrEmpty(list))
                    return intermediary.model[intermediary.name] ?? "";
                const match = list.find(x => x.Key.toString() === intermediary.model[intermediary.name]?.toString());
                if (match)
                    return match.Value;
                return intermediary.model[intermediary.name] ?? "";
            }
            return self.$gridMetadataService.setValue(intermediary);
        }
        function rowCommands(gridMetadata: GridMetadata, model: any): IMetadataAction[]
        {
            const commands = [];
            if (gridMetadata.CanCopy)
            {
                commands.push(<IMetadataAction>{
                    Label: "Copy",
                    Caption: "Copy",
                });
            }
            if (Utilities.IsNullOrUndefined(gridMetadata.Actions)) return commands;
            gridMetadata.Actions.forEach(a =>
            {
                const addAction: IMetadataAction = Utilities.deepCopy(a);
                addAction.Caption = self.$metadataService.EvaluateString(a.Label, model.$$Parent, model);
                addAction.Hidden = self.$metadataPropertyService.isHidden(
                    <PropertyMetadata>{
                        HiddenProperty: a.HiddenProperty,
                        Hidden: a.Hidden
                    },
                    model,
                    model.$$Parent
                );
                addAction.Disabled = self.$metadataPropertyService.isDisabled(
                    <PropertyMetadata>{
                        DisabledProperty: a.DisabledProperty,
                        Disabled: a.Disabled
                    },
                    model,
                    model.$$Parent
                );
                commands.push(addAction);
            });
            return commands;
        }
        function setSelected(id: string): boolean
        {
            const providerId = viewMetadata.GridProperties["$providerId"];
            const selected = selectProperty ? entity[selectProperty] : entity["Selected"];
            if (!self.$gridSelectionService.SelectedIds[providerId])
                return selected;
            const index = self.$gridSelectionService.SelectedIds[providerId].indexOf(id);
            if (selected)
            {
                if (index < 0)
                    self.$gridSelectionService.AddSelected(providerId, id);
            }
            else
            {
                if (index > -1)
                    self.$gridSelectionService.RemoveSelected(providerId, index);
            }
            return selected;
        }

        return item;
    }

    private loadChildren(
        entities: any[],
        viewMetadata: IGridViewMetadata,
        multiselectActions: IMetadataAction[],
        filteredColumns: string[],
        includeLookups?: string[],
        forFilter?: boolean
    ): Promise<any[]>
    {
        const self = this;
        const loaders: Promise<any>[] = [];
        const properties = propertiesToInclude();

        for (let i = 0; i < entities.length; i++)
        {
            const index = i;
            if (!entities[i]["$processed"])
            {
                loaders.push(
                    this.$metadataService
                        .VerifyLoadChildren(
                            properties,
                            viewMetadata.GridProperties.ClassMetadata.TypeName,
                            entities[i],
                            viewMetadata.SelectProperty ? viewMetadata.SelectProperty : "Selected"
                        )
                        .then(r => (entities[index] = r))
                );
            }
        }
        return Promise.all(loaders)
            .then(() =>
            {
                return self.loadRows(
                    entities,
                    viewMetadata,
                    multiselectActions,
                    filteredColumns,
                    includeLookups,
                    forFilter
                );
            });

        function propertiesToInclude(): PropertyMetadata[]
        {
            const properties = viewMetadata.GridProperties.ClassMetadata.Properties.filter(x => !!x.ClassMetadata && x.ClassMetadata.Properties.length > 0 && !self.$metadataService.IsHiddenProperty(x));
            if (properties.length > 0 && filteredColumns?.length > 0)
                return properties.filter(p => filteredColumns.indexOf(`${p.Name}.${p.ClassMetadata.Properties[0].Name}`) > -1);
            return properties;
        }
    }

    private columnVisible(intermediary: MetadataIntermediary): boolean
    {
        if (intermediary.property.Hidden) return false;
        if (!Utilities.IsNullOrUndefinedOrEmpty(intermediary.property.HiddenProperty))
            return !this.$metadataService.EvaluateBoolean(
                intermediary.property.HiddenProperty,
                intermediary.model["$$Parent"],
                intermediary.model
            );
        return true;
    }

    private alwaysHidden(intermediary: MetadataIntermediary): boolean
    {
        if (intermediary.property.DrawWidth === -2)
            return true;
        if (!Utilities.IsNullOrUndefinedOrEmpty(intermediary.property.HiddenProperty))
            return this.$metadataService.EvaluateBoolean(
                intermediary.property.HiddenProperty,
                intermediary.model["$$Parent"],
                intermediary.model);
        return false;
    }

    private isFilterable(property: PropertyMetadata, serverPaging: boolean): boolean
    {
        if (!serverPaging) return true;
        return !property.UnMapped;
    }

}
